import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, HelpCircle } from "lucide-react";

const faqs = [
  {
    q: "What does a web design and SEO agency actually do?",
    a: "We plan, design, and build your website with a search-friendly foundation, then continually optimize content, structure, and conversion paths so more of the right visitors find you and become customers. Think of us as your outsourced growth team for design, SEO, AEO, and analytics.",
  },
  {
    q: "What is AEO (Answer Engine Optimization) and how is it different from SEO?",
    a: "SEO gets you ranked on Google and Bing search results. AEO — Answer Engine Optimization — gets your business cited inside AI answer engines like ChatGPT, Perplexity, Google AI Overviews, and Gemini. It focuses on clear question-and-answer content, structured data, entity clarity, and authority signals that generative engines actually quote.",
  },
  {
    q: "What is GEO (Generative Engine Optimization)?",
    a: "GEO is the practice of optimizing content so that generative AI models pull, cite, or recommend your brand in their responses. It overlaps with AEO and traditional SEO but places extra weight on semantic clarity, first-hand expertise, structured data (Schema.org), and citation-worthy formatting.",
  },
  {
    q: "How long does it take to see SEO and AEO results?",
    a: "Technical and on-page improvements typically lift Google rankings within 4–8 weeks. Content-driven traffic and topical authority compound between months 3 and 6, with strongest gains at 6–12 months. AI-search citations follow a similar curve as your entity and content graph solidify.",
  },
  {
    q: "How much does a new website cost?",
    a: "APEX projects start from $2,400 for a launch-ready marketing site, $5,800 for our Growth package (design + SEO + content), and custom pricing for the Apex Pro program. Every quote is scoped after a free strategy call.",
  },
  {
    q: "Do you work with local businesses?",
    a: "Yes. We ship local-SEO-ready sites with service-area pages, Google Business Profile optimization, LocalBusiness schema, review generation flows, and location-specific landing pages so nearby buyers find you first.",
  },
  {
    q: "Can APEX help my site show up in ChatGPT and Perplexity?",
    a: "Yes. Our AEO program combines structured data (Organization, Product, FAQ, HowTo, Article schema), entity-based content, semantic clustering, authoritative citations, and consistent brand mentions across trusted sources so AI answer engines are more likely to reference your brand.",
  },
  {
    q: "Do you handle content writing and copy?",
    a: "Yes. Our content strategists and writers produce SEO briefs, long-form articles, landing page copy, and conversion-focused microcopy. Every piece is written for humans first and structured for both Google and AI answer engines.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="section-pad relative">
      <div className="container-apex">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-5">
            <div className="eyebrow mb-4"><span className="w-6 h-px bg-apex-cyan" /> Answers, in plain English</div>
            <h2 className="text-4xl md:text-5xl font-extrabold leading-[1.05]">
              FAQs about web design, SEO, and <span className="text-apex-orange">AI search.</span>
            </h2>
            <p className="mt-5 text-white/70 text-lg leading-relaxed">
              These are the questions clients — and increasingly, AI search engines — ask us most. Structured so both humans and answer engines can quote us cleanly.
            </p>
            <div className="mt-8 glass-tint-cyan p-5 flex items-start gap-3">
              <HelpCircle className="w-5 h-5 text-apex-cyan mt-0.5" strokeWidth={1.75} />
              <div>
                <div className="font-heading font-semibold text-sm">Can't find your question?</div>
                <a href="#contact" className="text-sm text-apex-cyanSoft hover:text-white underline underline-offset-4">
                  Book a free strategy call →
                </a>
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            <ul className="space-y-3">
              {faqs.map((item, i) => {
                const isOpen = open === i;
                return (
                  <li key={item.q}>
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.5, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                      className={`glass ${isOpen ? "border-white/20 bg-white/[0.06]" : ""} overflow-hidden`}
                    >
                      <button
                        type="button"
                        onClick={() => setOpen(isOpen ? -1 : i)}
                        aria-expanded={isOpen}
                        data-testid={`faq-toggle-${i}`}
                        className="w-full flex items-center justify-between gap-4 text-left px-5 md:px-6 py-5"
                      >
                        <span className="font-heading font-semibold text-[15px] md:text-base leading-snug">{item.q}</span>
                        <span className={`shrink-0 w-8 h-8 rounded-full border border-white/15 flex items-center justify-center transition-all ${isOpen ? "bg-apex-orange border-apex-orange rotate-45" : "bg-white/[0.04]"}`}>
                          <Plus className="w-4 h-4" strokeWidth={2.2} />
                        </span>
                      </button>
                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <motion.div
                            key="body"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            className="px-5 md:px-6"
                          >
                            <p className="pb-5 text-sm md:text-[15px] text-white/75 leading-relaxed">{item.a}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
